import { flush, startSpanManual } from '@sentry/react';
import type { BrowserClient, Span } from '@sentry/react';
import { eventTracker } from './EventTracker';
import { transactionTracker } from './TransactionTracker';
import { userDataTracker } from './UserDataTracker';
import { sectionNavigationTracker } from './SectionNavigationTracker';
import { dynamicSectionsTracker } from './DynamicSectionsTracker';

/**
 * Defines operation types for Sentry transaction spans.
 */
enum SentrySpanOperationTypes {
    LOBBY_SESSION = 'egt.lobby.session',
}

/**
 * Defines predefined transaction types for Sentry monitoring.
 */
const SentryTransactionTypes = {
    LOBBY_SESSION: {
        name: 'lobby-session',
        op: SentrySpanOperationTypes.LOBBY_SESSION,
    },
} as const;

/**
 * Defines attribute keys used for adding metadata to Sentry spans.
 */
const SENTRY_DATA = {
    CONFIGURATIONS: {
        GAME_KEY: 'egt.configuration.gameKey', // Can be used for Lobby ID or similar
        DEMO: 'egt.configuration.demo',
        MODE: 'egt.configuration.mode',
        VERSION: 'egt.configuration.version',
        LANGUAGE: 'egt.configuration.language',
        CURRENCY: 'egt.configuration.currency',
    },
    PERFORMANCE: {
        MEMORY: {
            USED_JSHEAP_SIZE: 'egt.performance.memory.used_jsheap_size',
            TOTAL_JSHEAP_SIZE: 'egt.performance.memory.total_jsheap_size',
            JSHEAP_SIZE_LIMIT: 'egt.performance.memory.jsheap_size_limit',
        },
        DEVICE: {
            MODEL: 'egt.performance.device.model',
            VENDOR: 'egt.performance.device.vendor',
            PLATFORM: 'egt.performance.device.platform',
        },
    },
    SESSION: {
        OPEN_LOBBY: 'egt.session.open_lobby',
        PAGE_HIDDEN: 'egt.session.page_hidden',
        ORIENTATION: 'egt.session.orientation',
        SCREEN_WIDTH: 'egt.session.screen_width',
        SCREEN_HEIGHT: 'egt.session.screen_height',
        START_DATE_UTC: 'egt.session.start_date_utc',
        DOMAIN_NAME: 'egt.session.domain_name',
    },
    UI: {
        INTERACTIONS: {
            SEARCH_QUERIES: 'egt.lobby.ui.interactions.search_queries',
        },
    },
    TRANSACTIONS: {
        GAME_CLIENT: {
            GAME_NAME: 'egt.lobby.transactions.game_client.game_name',
            GAME_KEY: 'egt.lobby.transactions.game_client.game_key',
            GAME_OPENED: 'egt.lobby.transactions.game_client.game_opened',
            GAME_OPENED_FROM_SECTION: 'egt.lobby.transactions.game_client.game_opened_from_section',
            GAME_OPENED_FROM_MULTI_VIEW: 'egt.lobby.transactions.game_client.game_opened_from_multi_view',
            GAME_OPENED_FROM_SINGLE_VIEW: 'egt.lobby.transactions.game_client.game_opened_from_single_view',
        },
    },
    SECTION_NAVIGATION: {
        LAST_SECTION: 'egt.lobby.section_navigation.last_section',
        LAST_SOURCE: 'egt.lobby.section_navigation.last_source',
        ALL_LINK_COUNT: 'egt.lobby.section_navigation.all_link_count',
        FOOTER_NAVIGATION_COUNT: 'egt.lobby.section_navigation.footer_navigation_count',
        WIDGET_COUNT: 'egt.lobby.section_navigation.widget_count',
        BANNER_COUNT: 'egt.lobby.section_navigation.banner_count',
        HISTORY: 'egt.lobby.section_navigation.history',
    },
    USER_DATA: {
        PLAYER_ID: 'egt.lobby.user_data.player_id',
        USER_SESSION: 'egt.lobby.user_data.user_session',
        USER_SETTINGS: {
            SOUND: 'egt.lobby.user_data.user_settings.sound',
            LANGUAGE: 'egt.lobby.user_data.user_settings.language',
        },
    },
} as const;

class SessionService {
    private static instance: SessionService;
    private sentryClient: BrowserClient | undefined;
    private currentSpan: Span | undefined;
    private searchQueries: string[] = [];

    // Store event listener references for cleanup
    private eventListeners: Array<{
        target: EventTarget;
        event: string;
        handler: EventListener;
    }> = [];

    private constructor() {}

    public static getInstance(): SessionService {
        if (!SessionService.instance) {
            SessionService.instance = new SessionService();
        }
        return SessionService.instance;
    }

    public init(client: BrowserClient) {
        this.sentryClient = client;
        this.setupEventListeners();
        this.startSession();
    }

    private addEventListenerWithTracking(
        target: EventTarget,
        event: string,
        handler: EventListener
    ) {
        target.addEventListener(event, handler);
        this.eventListeners.push({ target, event, handler });
    }

    private setupEventListeners() {
        if (typeof window !== 'undefined') {
            // Session end events
            this.addEventListenerWithTracking(window, 'pagehide', () => {
                this.handleSessionEnd();
            });

            this.addEventListenerWithTracking(document, 'visibilitychange', () => {
                if (document.visibilityState === 'hidden') {
                    this.handleSessionEnd();
                }
            });

            // Orientation tracking events
            this.addEventListenerWithTracking(window, 'orientationchange', () => {
                this.updateOrientation();
            });

            this.addEventListenerWithTracking(window, 'resize', () => {
                this.updateOrientation();
            });
        }
    }

    private handleSessionEnd() {
        if (this.currentSpan) {
            // Track with consistent naming: exit_browser (not a click)
            eventTracker.trackEvent('exit_browser');

            // Add event tracking data before ending session
            this.attachEventData();
            this.currentSpan.setAttribute(SENTRY_DATA.SESSION.PAGE_HIDDEN, 'true');
            this.endSession();
        }
        flush(500);
    }

    public startSession(attributes?: Record<string, string | boolean | number>) {
        if (!this.sentryClient) return;

        this.startTransaction(SentryTransactionTypes.LOBBY_SESSION, attributes);

        // Initialize screen tracking after starting the transaction
        this.initializeScreenTracking();
    }

    public endSession() {
        if (this.currentSpan) {
            this.currentSpan.setStatus({ code: 1 }); // OK
            this.currentSpan.end();
            this.currentSpan = undefined;
        }
    }

    public startTransaction(
        transactionType: (typeof SentryTransactionTypes)[keyof typeof SentryTransactionTypes],
        attributes?: Record<string, string | boolean | number>
    ) {
        if (!this.sentryClient) return;

        if (this.currentSpan) {
            this.endSession();
        }

        this.currentSpan = startSpanManual(
            {
                name: transactionType.name,
                op: transactionType.op,
                parentSpan: null, // More explicit than @ts-ignore
            },
            (span) => {
                return span;
            }
        );

        // Add UTC start date (date only, no time)
        this.currentSpan?.setAttribute(
            SENTRY_DATA.SESSION.START_DATE_UTC,
            new Date().toISOString().split('T')[0]
        );

        if (attributes) {
            Object.keys(attributes).forEach((key) => {
                this.currentSpan?.setAttribute(key, attributes[key]);
            });
        }

        return this.currentSpan;
    }

    public getActiveSpan() {
        return this.currentSpan;
    }
    
    public updateSessionData(key: string, value: string | boolean | number) {
        if (this.currentSpan) {
            this.currentSpan.setAttribute(key, value);
        }
    }

    /**
     * Set the domain name from apiHost (strips https:// prefix)
     */
    public setDomainName(apiHost: string) {
        if (this.currentSpan && apiHost) {
            const domainName = apiHost.replace(/^https?:\/\//, '');
            this.currentSpan.setAttribute(SENTRY_DATA.SESSION.DOMAIN_NAME, domainName);
        }
    }

    /**
     * Attach event tracking data to the current session
     */
    private attachEventData() {
        if (!this.currentSpan) return;

        const eventData = eventTracker.getFormattedCounts();

        // Add each event count as a separate attribute
        Object.entries(eventData).forEach(([key, value]) => {
            this.currentSpan?.setAttribute(key, value);
        });

        // Attach transaction data
        this.attachTransactionData();

        // Attach user data
        this.attachUserData();

        // Attach section navigation data
        this.attachSectionNavigationData();

        // Attach dynamic sections data
        this.attachDynamicSectionsData();
    }

    /**
     * Attach transaction tracking data to the current session
     */
    private attachTransactionData() {
        if (!this.currentSpan) return;

        const transactionData = transactionTracker.getFormattedTransactions();

        // Add each transaction attribute
        Object.entries(transactionData).forEach(([key, value]) => {
            this.currentSpan?.setAttribute(key, value);
        });
    }

    /**
     * Attach user data to the current session
     */
    private attachUserData() {
        if (!this.currentSpan) return;

        const userData = userDataTracker.getFormattedUserData();

        // Add each user data attribute
        Object.entries(userData).forEach(([key, value]) => {
            this.currentSpan?.setAttribute(key, value);
        });
    }

    /**
     * Attach section navigation tracking data to the current session
     */
    private attachSectionNavigationData() {
        if (!this.currentSpan) return;

        const navigationData = sectionNavigationTracker.getFormattedData();

        Object.entries(navigationData).forEach(([key, value]) => {
            this.currentSpan?.setAttribute(key, value);
        });
    }

    /**
     * Attach dynamic-sections tracking data to the current session
     */
    private attachDynamicSectionsData() {
        if (!this.currentSpan) return;

        const dynamicSectionsData = dynamicSectionsTracker.getFormattedData();

        Object.entries(dynamicSectionsData).forEach(([key, value]) => {
            this.currentSpan?.setAttribute(key, value);
        });
    }

    /**
     * Manually attach event data (useful for long sessions)
     * @deprecated Use updateEventData instead
     */
    public updateClickData() {
        this.attachEventData();
    }

    /**
     * Manually attach event data (useful for long sessions)
     */
    public updateEventData() {
        this.attachEventData();
    }

    /**
     * Get screen dimensions (logical pixels for mobile)
     * Returns width and height as separate numeric values
     */
    private getScreenDimensions(): { width: number; height: number } | null {
        if (typeof window === 'undefined') return null;

        // Use window.screen.width/height which gives logical pixels on mobile
        // This respects device pixel ratio and gives us CSS pixels, not physical pixels
        return {
            width: window.screen.width,
            height: window.screen.height,
        };
    }

    /**
     * Get current orientation based on window dimensions
     * Returns "portrait" or "landscape"
     */
    private getOrientation(): string {
        if (typeof window === 'undefined') return 'unknown';

        // Use window dimensions for more reliable detection
        // window.orientation is deprecated
        const width = window.innerWidth;
        const height = window.innerHeight;

        return width > height ? 'landscape' : 'portrait';
    }

    /**
     * Update screen resolution in session data
     */
    public updateScreenResolution() {
        const dimensions = this.getScreenDimensions();
        if (dimensions) {
            this.updateSessionData(SENTRY_DATA.SESSION.SCREEN_WIDTH, dimensions.width);
            this.updateSessionData(SENTRY_DATA.SESSION.SCREEN_HEIGHT, dimensions.height);
        }
    }

    /**
     * Update orientation in session data
     */
    public updateOrientation() {
        const orientation = this.getOrientation();
        this.updateSessionData(SENTRY_DATA.SESSION.ORIENTATION, orientation);
    }

    /**
     * Initialize screen tracking (resolution and orientation)
     * Call this when starting a session
     */
    public initializeScreenTracking() {
        this.updateScreenResolution();
        this.updateOrientation();
    }

    /**
     * Clean up event listeners to prevent memory leaks
     */
    private cleanupEventListeners() {
        this.eventListeners.forEach(({ target, event, handler }) => {
            target.removeEventListener(event, handler);
        });
        this.eventListeners = [];
    }

    /**
     * Cleanup method for proper teardown
     * Call this when the service is no longer needed
     */
    public cleanup() {
        this.endSession();
        this.cleanupEventListeners();
    }

    /**
     * Track a search query
     * Only adds if it's different from the last query and not empty
     */
    public trackSearchQuery(query: string) {
        const trimmedQuery = query.trim();

        // Don't track empty queries
        if (!trimmedQuery) return;

        // Don't track if it's the same as the last query (prevent duplicates)
        if (this.searchQueries.length > 0 &&
            this.searchQueries[this.searchQueries.length - 1] === trimmedQuery) {
            return;
        }

        // Add to search history
        this.searchQueries.push(trimmedQuery);

        // Update Sentry session data with the array of searches
        this.updateSearchQueries();
    }

    /**
     * Update search queries in session data
     */
    private updateSearchQueries() {
        if (this.currentSpan && this.searchQueries.length > 0) {
            this.currentSpan.setAttribute(
                SENTRY_DATA.UI.INTERACTIONS.SEARCH_QUERIES,
                this.searchQueries
            );
        }
    }

    /**
     * Get search queries (for debugging)
     */
    public getSearchQueries(): string[] {
        return [...this.searchQueries];
    }

    /**
     * Clear search queries (useful for new sessions)
     */
    public clearSearchQueries() {
        this.searchQueries = [];
    }
}

export const sessionService = SessionService.getInstance();
